<template>
  <b-sidebar
    v-model="isVisible"
    right
    :width="width"
    :no-header="!showHeader"
    backdrop
  >
    <!-- Header Section -->
    <template
      v-if="showHeader"
      #header
    >
      <div class="sidebar-header-wrapper">
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="flex-grow-1">
            <h5
              v-if="title"
              class="sidebar-title mb-0"
            >
              {{ title }}
            </h5>
            <slot name="header-left" />
          </div>
          <div class="d-flex align-items-center">
            <slot name="header-right" />
            <div
              class="sidebar-close-btn"
              @click="closeSidebar"
            >
              <feather-icon
                icon="XIcon"
                :size="'20'"
                class="close-icon"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Tabs Navigation -->
    <div
      v-if="showTabs && availableTabs.length > 0"
      class="sidebar-tabs-wrapper"
    >
      <b-nav
        tabs
        class="sidebar-tabs"
      >
        <b-nav-item
          v-for="tab in availableTabs"
          :key="tab.id"
          :active="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </b-nav-item>
      </b-nav>
    </div>
    <!-- Main Content -->
    <div class="sidebar-content-wrapper">
      <!-- Tab Content -->
      <div
        v-if="showTabs && availableTabs.length > 0"
        class="tab-content-wrapper"
      >
        <div
          v-for="tab in availableTabs"
          v-show="activeTab === tab.id"
          :key="tab.id"
          class="tab-pane"
        >
          <slot :name="tab.id" />
        </div>
      </div>
      <!-- Default slot (when tabs are disabled) -->
      <slot v-else />
    </div>

  </b-sidebar>
</template>

<script>
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'RightSidebar',
  components: {
    FeatherIcon,
  },
  props: {
    // Control sidebar visibility
    value: {
      type: Boolean,
      default: false,
    },
    // Sidebar title
    title: {
      type: String,
      default: '',
    },
    // Sidebar width (can be percentage or pixel value)
    width: {
      type: String,
      default: '400px',
    },
    // Show/hide header
    showHeader: {
      type: Boolean,
      default: true,
    },
    // Show/hide tabs
    showTabs: {
      type: Boolean,
      default: true,
    },
    // Tabs configuration array: [{ id: 'details', label: 'Details' }, ...]
    tabs: {
      type: Array,
      default: () => [
        { id: 'details', label: 'Details' },
        { id: 'information', label: 'Information' },
      ],
    },
  },
  data() {
    return {
      isVisible: this.value,
      activeTab: null,
    }
  },
  computed: {
    // Filter tabs to only show those with corresponding slots
    availableTabs() {
      if (!this.showTabs || !this.tabs || this.tabs.length === 0) {
        return []
      }

      // Check which tabs have corresponding slots
      return this.tabs.filter(tab =>
        // eslint-disable-next-line implicit-arrow-linebreak
        this.$scopedSlots[tab.id] || this.$slots[tab.id])
    },
  },
  watch: {
    value(val) {
      this.isVisible = val
    },
    isVisible(val) {
      this.$emit('input', val)
      // Set active tab to first available when sidebar opens
      if (val && this.availableTabs.length > 0 && !this.activeTab) {
        this.activeTab = this.availableTabs[0].id
      }
      // Clear data when sidebar closes
      if (!val) {
        // notify parent that sidebar is being hidden
        this.$emit('hide')
        this.clearData()
      }
    },
    // Update active tab when available tabs change
    availableTabs(newTabs) {
      if (newTabs.length > 0) {
        // If current active tab is not in available tabs, switch to first available
        if (!newTabs.find(tab => tab.id === this.activeTab)) {
          this.activeTab = newTabs[0].id
        }
      } else {
        this.activeTab = null
      }
    },
  },
  mounted() {
    // Set initial active tab to first available
    if (this.availableTabs.length > 0) {
      this.activeTab = this.availableTabs[0].id
    }
  },
  methods: {
    clearData() {
      // Reset active tab to first available tab
      if (this.availableTabs.length > 0) {
        this.activeTab = this.availableTabs[0].id
      } else {
        this.activeTab = null
      }
      // Emit event to parent to clear data
      this.$emit('clear-data')
    },
    closeSidebar() {
      this.isVisible = false
      this.clearData()
      this.$emit('close')
    },
    // Public method to open sidebar
    open() {
      this.isVisible = true
    },
    // Public method to close sidebar
    close() {
      this.isVisible = false
      this.clearData()
    },
    // Public method to toggle sidebar
    toggle() {
      this.isVisible = !this.isVisible
      if (!this.isVisible) {
        this.clearData()
      }
    },
  },
}
</script>

<style lang="scss">
// Sidebar Header Styling
.b-sidebar-header{
    padding: 0px !important;
    border-bottom: 1px solid #636363;
  }
.sidebar-header-wrapper {
  background: white;
  padding: 1.25rem 1.5rem;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;

  .sidebar-title {
    color: #636363;
    font-weight: 600;
    font-size: 1.125rem;
    letter-spacing: 0.3px;
    margin: 0;
  }

  .sidebar-close-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: #636363;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-left: 0.75rem;

    &:hover {
      background-color:#636363;
      transform: rotate(90deg);
    }

    .close-icon {
      color: #ffffff;
    }
  }
}

// Tabs Navigation
.sidebar-tabs-wrapper {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e8ecef;
  position: sticky;
  top: 0;
  z-index: 9;

  .sidebar-tabs {
    margin-bottom: 0;

    .nav-item {
      flex: 1;

      .nav-link {
        text-align: center;
        padding: 0.75rem 1rem;
        color: #6e6b7b;
        padding: 20px 0px;
        transition: all 0.3s ease;
        background-color: #f5f5f5;
        border-radius: 30px 30px 0px 0px;

        &:hover {
          color: #297cd6;
          background-color: rgba(41, 124, 214, 0.05);
        }

        &.active {
          color: #297cd6;
          font-weight: 600;
          background: white !important;
          }
      }
    }
  }

}

// Sidebar Content Wrapper
.sidebar-content-wrapper {
  height: calc(100vh - 130px);
  overflow-y: auto;
  overflow-x: hidden;

  // Firefox scrollbar
  scrollbar-width: none ;

  // Tab content
  .tab-content-wrapper {
    .tab-pane {
      animation: fadeIn 0.3s ease-in;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
